import React, { useEffect, useState } from 'react';

import { faBuilding } from '@fortawesome/pro-solid-svg-icons';
import { ButtonGroup } from '@buildoutinc/blueprint-react/ui/ButtonGroup';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { get } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';
import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import LoadingSpinner from 'components/ui/loading_spinner';
import PageHeader from 'components/ui/page_header';

import StatusBadge from './status_badge';
import UploadForm from './upload_form';
import { STATUS_LABELS, type AcfrReport, type AcfrStatus } from './types';

interface Props {
  paths: { reports: string };
}

const FILTERS: (AcfrStatus | '')[] = ['', 'needs_review', 'extracted', 'failed', 'reviewed', 'no_employer_table'];

export default function AcfrReportsIndex({ paths }: Props) {
  const [reports, setReports] = useState<AcfrReport[] | null>(null);
  const [status, setStatus] = useState<AcfrStatus | ''>('needs_review');
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setReports(null);
      setErrors([]);
      try {
        const result: { reports: AcfrReport[] } = await get(paths.reports, { params: { status } });
        if (!cancelled) setReports(result.reports);
      } catch (error) {
        if (!cancelled) setErrors(requestErrors(error));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [paths.reports, status]);

  return (
    <>
      <PageHeader>
        <PageHeader.Heading
          description="Check the employers extracted from each county's ACFR before documents use them."
          icon={faBuilding}
          title="ACFR principal employers"
        />
      </PageHeader>

      <div className="container-fluid py-4">
        <div className="row g-4">
          <div className="col-lg-9 d-flex flex-column gap-3">
            <ButtonGroup>
              {FILTERS.map(filter => (
                <Button
                  key={filter || 'all'}
                  label={filter ? STATUS_LABELS[filter] : 'All'}
                  variant={filter === status ? 'primary' : 'outline'}
                  onClick={() => setStatus(filter)}
                />
              ))}
            </ButtonGroup>

            <Errors errors={errors} />
            {reports && <ReportsTable reports={reports} />}
            {!reports && errors.length === 0 && <LoadingSpinner />}
          </div>

          <div className="col-lg-3">
            <UploadForm reportsPath={paths.reports} />
          </div>
        </div>
      </div>
    </>
  );
}

interface ReportsTableProps {
  reports: AcfrReport[];
}

function ReportsTable({ reports }: ReportsTableProps) {
  if (reports.length === 0) return <p className="text-muted">No reports.</p>;

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.Head>County</Table.Head>
          <Table.Head>FIPS</Table.Head>
          <Table.Head>Fiscal year</Table.Head>
          <Table.Head>Status</Table.Head>
          <Table.Head>Source</Table.Head>
          <Table.Head>Updated</Table.Head>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {reports.map(report => (
          <Table.Row key={report.id}>
            <Table.Cell>
              <a href={report.path}>{[report.countyName || 'Unknown county', report.state].join(', ')}</a>
            </Table.Cell>
            <Table.Cell>{report.countyFips}</Table.Cell>
            <Table.Cell>{report.fiscalYear ?? '—'}</Table.Cell>
            <Table.Cell>
              <StatusBadge status={report.status} />
              {report.reviewReasons.length > 0 && (
                <div className="small text-muted">{report.reviewReasons.join(', ')}</div>
              )}
            </Table.Cell>
            <Table.Cell>
              {report.sourceUrl ? (
                <a href={report.sourceUrl} rel="noopener noreferrer" target="_blank">
                  {new URL(report.sourceUrl).hostname}
                </a>
              ) : (
                report.source === 'uploaded' && 'Uploaded'
              )}
            </Table.Cell>
            <Table.Cell>{new Date(report.updatedAt).toLocaleDateString()}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
